<template>
  <div class="client-multi-check">
    <kt-autoselect
      :mode="props.mode"
      :label="props.label"
      :options="clientOptions"
      display-by="Company"
      value-by="CustomerNumber"
      :multiple="true"
      v-model="model"
      search-placeholder="Search clients..."
      :empty-text="props.emptyText"
      :on-refresh="handleRefresh"
      @change="handleChange"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, type PropType } from 'vue';
import KtAutoselect from '@/components/UI/KtAutoselect.vue';
import { useClientStore } from '@/stores/ClientStore';
import type { ClientModel } from '@/models/ClientModel';

const props = defineProps({
  label: { 
    type: String, 
    default: 'Select Clients' 
  },
  mode: {
    type: String,
    default: "dropdown"
  },
  filter: {
    type: Function as PropType<(client: ClientModel) => boolean>,
    default: null
  },
  emptyText: {
    type: String,
    default: 'No Matching Clients'
  }
});

const emit = defineEmits<{
  change: [value: number[]];
}>();

const model = defineModel<number[]>({
  default: () => []
});

const clientStore = useClientStore();

// Raw list from the store. `clientOptions` derives from it so the filter
// prop is re-applied reactively whenever either the raw list or any reactive
// state the filter reads (e.g. a parent's availableClientIds set) changes.
const rawClients = ref<ClientModel[]>([]);

const clientOptions = computed<ClientModel[]>(() =>
  props.filter ? rawClients.value.filter(props.filter) : rawClients.value
);

// Load clients on mount
onMounted(async () => {
  // The store auto-initializes, just wait for it
  await new Promise(resolve => setTimeout(resolve, 100));
  rawClients.value = clientStore.list || [];
});

const handleChange = (value: number[]) => {
  emit('change', value);
};

const handleRefresh = async () => {
  await clientStore.refresh();
  rawClients.value = clientStore.list || [];
};
</script>
